import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getUsersDeleted, getUserDeletedByCode, resetUser } from "../../../../API/users";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import UserSearch from "../UserSearch/UserSearch";
import Columns from "./columns";
import dayjs from 'dayjs';

import { useAuth } from "../../../Context/AuthContext";

const UsersDeleted = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [usersDeleted, setUsersDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchUserText, setSearchUserText] = useState('');

  useEffect(() => {
    const fetchUsersDeleted = async () => {
      try {
        const data = await getUsersDeleted();
        setUsersDeleted(data);
        setFilteredData(data);
      } catch (error) {
        message.error("No se encontraron los usuarios eliminados.");
      }
    };

    fetchUsersDeleted();
  }, []);

  const breadCrumbUrl = [
    { label: "Usuarios", url: "/user-management" },
    { label: "Eliminados" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getUserDeletedByCode(record.id);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const handleReactivateUser = async (record) => {
    try {
      await resetUser(record.id);
      api.success({
        message: "Usuario reactivado",
        description: `${record.username} fue reactivado correctamente.`,
      });

      // Actualiza la lista local removiendo el usuario reactivado
      setUsersDeleted((prev) => prev.filter(u => u.id !== record.id));
      setFilteredData((prev) => prev.filter(u => u.id !== record.id));
    } catch (error) {
      api.error({
        message: "Error al reactivar usuario",
        description: error.response?.data?.user_id || "No se pudo reactivar el usuario.",
        key: 'user-reactivate-error'
      });
    }
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      //hasAnyRole={hasAnyRole}
      requiredPermission="read"
      //currentPermission={canAssetsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="Usuarios Eliminados" icon="usermanagement" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <UserSearch
                usersData={usersDeleted}
                searchText={searchUserText}
                setSearchText={setSearchUserText}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Usuarios Eliminados.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Usuario: item.username,
                    Nombre: item.first_name,
                    Apellido: item.last_name,
                    Email: item.email,
                    Área: item.area.name,
                    'Rol Asignado': item.roles?.[0]?.name || '-',
                    'Último Inicio Sesión': item.last_login_app 
                      ? dayjs(item.last_login_app).format('DD/MM/YYYY HH:mm') 
                      : '-',
                    Estado: item.is_active ? 'Activo' : 'Inactivo'
                    }))
                  }
              />
            </Col>
          </Row>
        </div>
      {
        !usersDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, handleReactivateUser)}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />

            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default UsersDeleted;